import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FolderOpen, Cloud, Play, X, Search, Loader2, HardDrive,
  RefreshCw, CheckCircle2, WifiOff, Wifi, AlertTriangle, RotateCcw, FileVideo
} from 'lucide-react';
import Header from '../components/Layout/Header';

// ==========================================
// 配置
// ==========================================
const LOCAL_VIDEO_FOLDER = 'E:\\SteamLibrary\\steamapps\\workshop\\content\\431960';
const CLOUD_VIDEO_URL = 'https://pub-3300c5431c524c789f6aa30ae9bad4a9.r2.dev/1718609100867-1676619029-test.mp4';
const PAGE_SIZE = 100;
const VIDEO_LOAD_TIMEOUT = 15000;        // 视频加载超时 15 秒（含网络差）
const NATIVE_SCAN_TIMEOUT = 30000;        // 文件夹扫描超时 30 秒
const NATIVE_READ_TIMEOUT = 60000;        // 文件读取超时 60 秒
const MAX_RETRY = 2;                      // 最大重试次数

// ==========================================
// 类型定义
// ==========================================
interface LocalVideoFile {
  id: string;
  name: string;
  path: string;
  size: number;
  lastModified: number;
}

type VideoError = {
  code: string;
  message: string;
  canRetry: boolean;
} | null;

// ==========================================
// 工具函数
// ==========================================
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// 高效的 base64 解码（分块处理，避免大文件阻塞主线程）
async function base64ToBlob(base64: string, mimeType: string): Promise<Blob> {
  // 分块大小：每块 1MB（base64 字符约 1.3MB）
  const CHUNK_SIZE = 1024 * 1024;
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < base64.length; offset += CHUNK_SIZE) {
    const chunk = base64.slice(offset, offset + CHUNK_SIZE);
    const binaryString = atob(chunk);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    byteArrays.push(bytes);

    // 让出主线程，避免 UI 卡顿
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return new Blob(byteArrays, { type: mimeType || 'video/mp4' });
}

// 检测浏览器/网络状态
function checkNetworkStatus(): { online: boolean; connectionType?: string } {
  const nav = navigator as any;
  const online = navigator.onLine;

  if (nav.connection) {
    return {
      online,
      connectionType: nav.connection.effectiveType || nav.connection.type,
    };
  }
  return { online };
}

// ==========================================
// 主组件
// ==========================================
export default function DualPlayer() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ====== 基本状态 ======
  const [mode, setMode] = useState<'cloud' | 'local'>('cloud');
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>(CLOUD_VIDEO_URL);

  // ====== 本地文件列表 ======
  const [files, setFiles] = useState<LocalVideoFile[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<LocalVideoFile[]>([]);
  const [totalScanned, setTotalScanned] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [selectedFile, setSelectedFile] = useState<LocalVideoFile | null>(null);

  // ====== 扫描状态 ======
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<VideoError>(null);
  const [scanSuccess, setScanSuccess] = useState(false);

  // ====== 播放状态（统一管理）======
  const [isLoading, setIsLoading] = useState(false);
  const [videoError, setVideoError] = useState<VideoError>(null);
  const [isBuffering, setIsBuffering] = useState(false);  // 播放中的缓冲状态
  const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);

  // ====== 网络状态 ======
  const [networkStatus, setNetworkStatus] = useState(checkNetworkStatus());

  // ====== 虚拟滚动 ======
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // ====== 引用管理（避免闭包陈旧）======
  const loadingAbortRef = useRef<{ aborted: boolean } | null>(null);
  const timeoutTimerRef = useRef<number | null>(null);
  const retryCountRef = useRef(0);

  // ==========================================
  // 网络状态监听（实时更新）
  // ==========================================
  useEffect(() => {
    const handleOnline = () => setNetworkStatus(checkNetworkStatus());
    const handleOffline = () => setNetworkStatus(checkNetworkStatus());

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ==========================================
  // 初始化：切换到本地模式时自动扫描
  // ==========================================
  useEffect(() => {
    if (mode === 'local' && files.length === 0 && !isScanning) {
      // 延迟扫描，避免切换动画时阻塞
      const timer = setTimeout(() => {
        handleScanFolder();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================
  // 搜索过滤
  // ==========================================
  useEffect(() => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) {
      setFilteredFiles(files);
    } else {
      setFilteredFiles(
        files.filter((f) =>
          f.name.toLowerCase().includes(keyword) ||
          f.path.toLowerCase().includes(keyword)
        )
      );
    }
    setVisibleCount(PAGE_SIZE);
  }, [files, searchText]);

  // ==========================================
  // 虚拟滚动
  // ==========================================
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (remaining < 300 && visibleCount < filteredFiles.length) {
      setVisibleCount((c) => Math.min(c + PAGE_SIZE, filteredFiles.length));
    }
  }, [visibleCount, filteredFiles.length]);

  // ==========================================
  // 清理定时器 & Blob URL（组件卸载）
  // ==========================================
  useEffect(() => {
    return () => {
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
      }
      if (videoBlobUrl) {
        URL.revokeObjectURL(videoBlobUrl);
      }
    };
  }, [videoBlobUrl]);

  // ==========================================
  // 设置播放超时（防止视频无限加载）
  // ==========================================
  const startVideoTimeout = useCallback(() => {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
    }
    timeoutTimerRef.current = window.setTimeout(() => {
      if (isLoading || videoRef.current?.readyState < 2) {
        setVideoError({
          code: 'TIMEOUT',
          message: '视频加载超时，请检查网络或重试',
          canRetry: true,
        });
        setIsLoading(false);
        setIsBuffering(false);
      }
    }, VIDEO_LOAD_TIMEOUT);
  }, [isLoading]);

  const clearVideoTimeout = useCallback(() => {
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  // ==========================================
  // 视频事件监听（统一处理加载/错误/缓冲）
  // ==========================================
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlers = {
      loadstart: () => {
        setIsLoading(true);
        setIsBuffering(false);
        setVideoError(null);
        startVideoTimeout();
      },
      loadeddata: () => {
        clearVideoTimeout();
        setIsLoading(false);
        setIsBuffering(false);
        setVideoError(null);
      },
      canplay: () => {
        clearVideoTimeout();
        setIsLoading(false);
        setIsBuffering(false);
        setVideoError(null);
      },
      waiting: () => {
        // 播放中遇到缓冲（网络慢）
        setIsBuffering(true);
      },
      playing: () => {
        setIsBuffering(false);
        clearVideoTimeout();
      },
      stalled: () => {
        // 网络停滞
        setIsBuffering(true);
      },
      progress: () => {
        // 有进度说明网络是通的
        if (isLoading) clearVideoTimeout();
      },
      error: (e: Event) => {
        clearVideoTimeout();
        setIsLoading(false);
        setIsBuffering(false);

        // 根据错误类型给出不同提示
        const mediaError = (e.target as HTMLVideoElement).error;
        let errorMsg = '视频加载失败';
        let canRetry = true;

        if (mediaError) {
          switch (mediaError.code) {
            case MediaError.MEDIA_ERR_ABORTED:
              errorMsg = '视频加载被中断';
              canRetry = true;
              break;
            case MediaError.MEDIA_ERR_NETWORK:
              errorMsg = '网络错误，请检查网络连接后重试';
              canRetry = true;
              break;
            case MediaError.MEDIA_ERR_DECODE:
              errorMsg = '视频解码失败，文件可能已损坏或格式不支持';
              canRetry = false;
              break;
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
              errorMsg = '视频格式或源不支持，无法播放';
              canRetry = false;
              break;
            default:
              errorMsg = '未知错误：' + (mediaError.message || '');
              canRetry = true;
          }
        }

        setVideoError({ code: 'VIDEO_ERROR', message: errorMsg, canRetry });
      },
    };

    video.addEventListener('loadstart', handlers.loadstart);
    video.addEventListener('loadeddata', handlers.loadeddata);
    video.addEventListener('canplay', handlers.canplay);
    video.addEventListener('waiting', handlers.waiting);
    video.addEventListener('playing', handlers.playing);
    video.addEventListener('stalled', handlers.stalled);
    video.addEventListener('progress', handlers.progress);
    video.addEventListener('error', handlers.error as EventListener);

    return () => {
      video.removeEventListener('loadstart', handlers.loadstart);
      video.removeEventListener('loadeddata', handlers.loadeddata);
      video.removeEventListener('canplay', handlers.canplay);
      video.removeEventListener('waiting', handlers.waiting);
      video.removeEventListener('playing', handlers.playing);
      video.removeEventListener('stalled', handlers.stalled);
      video.removeEventListener('progress', handlers.progress);
      video.removeEventListener('error', handlers.error as EventListener);
    };
  }, [startVideoTimeout, clearVideoTimeout, isLoading]);

  // ==========================================
  // 扫描本地文件夹（带超时保护）
  // ==========================================
  const handleScanFolder = useCallback(async () => {
    setIsScanning(true);
    setScanError(null);
    setScanSuccess(false);

    try {
      const nativeApi = (window as any).__NATIVE_API__;
      if (!nativeApi?.scanLocalVideos) {
        throw new Error('请在 APP 内使用本地视频功能，或使用 R2 云端视频');
      }

      // 创建带超时的 Promise
      const timeoutPromise = new Promise<LocalVideoFile[]>((_, reject) => {
        setTimeout(() => reject(new Error('扫描超时，文件夹可能过大或无权限')), NATIVE_SCAN_TIMEOUT);
      });

      const scanPromise = nativeApi.scanLocalVideos(LOCAL_VIDEO_FOLDER);
      const result = await Promise.race([scanPromise, timeoutPromise]);

      if (result.success && result.files && result.files.length > 0) {
        const sortedFiles = [...result.files].sort((a: LocalVideoFile, b: LocalVideoFile) =>
          a.name.localeCompare(b.name)
        );
        setFiles(sortedFiles);
        setTotalScanned(sortedFiles.length);
        setTotalSize(result.totalSize || 0);
        setScanSuccess(true);
      } else if (result.success) {
        setScanError({
          code: 'NO_VIDEOS',
          message: '未在该文件夹中找到视频文件',
          canRetry: true,
        });
      } else {
        throw new Error(result.message || '扫描失败');
      }
    } catch (err: any) {
      console.error('扫描失败:', err);
      setScanError({
        code: 'SCAN_ERROR',
        message: err.message || '扫描文件夹失败',
        canRetry: true,
      });
    } finally {
      setIsScanning(false);
    }
  }, []);

  // ==========================================
  // 播放本地视频（带超时、重试、分块解码）
  // ==========================================
  const handlePlayFile = useCallback(async (file: LocalVideoFile, retryAttempt: number = 0) => {
    // 防止并发请求
    if (isLoading) return;

    // 清理上一个视频的资源
    clearVideoTimeout();
    if (videoBlobUrl) {
      URL.revokeObjectURL(videoBlobUrl);
      setVideoBlobUrl(null);
    }

    // 初始化状态
    setSelectedFile(file);
    setVideoError(null);
    setIsLoading(true);
    setIsBuffering(false);
    retryCountRef.current = retryAttempt;

    // 设置取消标记（用于用户中途切换视频时中断）
    const abortMarker = { aborted: false };
    loadingAbortRef.current = abortMarker;

    try {
      const nativeApi = (window as any).__NATIVE_API__;
      if (!nativeApi?.readLocalVideo) {
        throw new Error('原生方法不可用，请在 APP 中使用');
      }

      // 1. 超时保护
      const timeoutPromise = new Promise<any>((_, reject) => {
        setTimeout(() => {
          reject(new Error('读取超时，请重试'));
        }, NATIVE_READ_TIMEOUT);
      });

      // 2. 调用原生读取
      const readPromise = nativeApi.readLocalVideo(file.path);
      const result = await Promise.race([readPromise, timeoutPromise]);

      // 检查是否已被取消（用户点击了其他视频）
      if (abortMarker.aborted) return;

      if (!result.success) {
        throw new Error(result.message || '读取失败');
      }

      // 3. 高效 base64 → Blob 转换（分块处理）
      const mimeType = result.mimeType || getMimeTypeFromFileName(file.name);
      const blob = await base64ToBlob(result.data, mimeType);

      if (abortMarker.aborted) return;

      // 4. 创建 Object URL 并播放
      const objectUrl = URL.createObjectURL(blob);
      setVideoBlobUrl(objectUrl);
      setCurrentVideoUrl(objectUrl);

      // 5. 在下次 DOM 更新后调用 play
      setTimeout(() => {
        if (abortMarker.aborted) return;
        if (videoRef.current) {
          videoRef.current.src = objectUrl;
          videoRef.current.load();
          videoRef.current.play().catch(() => {
            // 自动播放被浏览器阻止，用户手动点击即可
          });
        }
      }, 100);

      // 清理加载状态（注意：video 的事件监听器会接管 canplay 等状态）
      // 这里设置一个后备：如果 2 秒后还在 loading，但视频元素已经能播放，则清除 loading
      setTimeout(() => {
        if (!abortMarker.aborted && videoRef.current && videoRef.current.readyState >= 2) {
          setIsLoading(false);
          setIsBuffering(false);
        }
      }, 2000);

    } catch (err: any) {
      console.error('播放失败:', err);

      if (abortMarker.aborted) return;

      // 自动重试逻辑
      if (retryAttempt < MAX_RETRY) {
        setVideoError({
          code: 'RETRYING',
          message: `加载失败，正在重试 (${retryAttempt + 1}/${MAX_RETRY})...`,
          canRetry: true,
        });
        setTimeout(() => {
          handlePlayFile(file, retryAttempt + 1);
        }, 1500);
        return;
      }

      setIsLoading(false);
      setVideoError({
        code: 'PLAY_ERROR',
        message: err.message || '视频加载失败，请点击其他视频或重试',
        canRetry: true,
      });
    }
  }, [isLoading, videoBlobUrl, clearVideoTimeout]);

  // ==========================================
  // 获取文件 MIME 类型（根据扩展名）
  // ==========================================
  function getMimeTypeFromFileName(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase();
    const map: Record<string, string> = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      mkv: 'video/x-matroska',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      flv: 'video/x-flv',
      wmv: 'video/x-ms-wmv',
      m4v: 'video/x-m4v',
    };
    return map[ext || ''] || 'video/mp4';
  }

  // ==========================================
  // 切换到云端视频
  // ==========================================
  const switchToCloud = useCallback(() => {
    // 取消当前加载中的操作
    if (loadingAbortRef.current) {
      loadingAbortRef.current.aborted = true;
      loadingAbortRef.current = null;
    }
    clearVideoTimeout();

    // 清理本地视频资源
    if (videoBlobUrl) {
      URL.revokeObjectURL(videoBlobUrl);
      setVideoBlobUrl(null);
    }

    setMode('cloud');
    setSelectedFile(null);
    setVideoError(null);
    setIsLoading(false);
    setIsBuffering(false);
    setCurrentVideoUrl(CLOUD_VIDEO_URL);

    // 让视频元素重新加载云端视频
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.src = CLOUD_VIDEO_URL;
        videoRef.current.load();
      }
    }, 100);
  }, [videoBlobUrl, clearVideoTimeout]);

  // ==========================================
  // 切换到本地模式
  // ==========================================
  const switchToLocal = useCallback(() => {
    // 取消当前可能的云端视频加载
    if (loadingAbortRef.current) {
      loadingAbortRef.current.aborted = true;
      loadingAbortRef.current = null;
    }
    clearVideoTimeout();
    setMode('local');
    setVideoError(null);
    setIsLoading(false);
    setIsBuffering(false);

    // 如果还没加载列表，自动开始扫描
    if (files.length === 0 && !isScanning) {
      setTimeout(() => handleScanFolder(), 300);
    }
  }, [files.length, isScanning, handleScanFolder, clearVideoTimeout]);

  // ==========================================
  // 重试当前视频
  // ==========================================
  const handleRetry = useCallback(() => {
    setVideoError(null);

    if (mode === 'cloud') {
      // 重新加载云端视频
      setIsLoading(true);
      setCurrentVideoUrl('');
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.src = CLOUD_VIDEO_URL;
          videoRef.current.load();
        }
        setCurrentVideoUrl(CLOUD_VIDEO_URL);
      }, 100);
    } else if (selectedFile) {
      // 重试当前本地视频
      handlePlayFile(selectedFile, 0);
    }
  }, [mode, selectedFile, handlePlayFile]);

  // ==========================================
  // 虚拟滚动：只渲染可见条目
  // ==========================================
  const visibleFiles = useMemo(() => {
    return filteredFiles.slice(0, visibleCount);
  }, [filteredFiles, visibleCount]);

  // ==========================================
  // 渲染
  // ==========================================
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Header title="双画面播放器" showBack onClickBack={() => navigate('/')} />

      <main className="flex-1 flex flex-col">
        <div className="flex-1 flex flex-col lg:flex-row">
          <div className="flex-1 min-h-0 flex flex-col">
            {/* ========== 顶部：模式切换 + 网络状态 ========== */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between px-4 py-3 gap-2 flex-wrap">
                {/* 模式切换按钮 */}
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={switchToCloud}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all text-sm ${
                      mode === 'cloud'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Cloud size={16} />
                    <span>R2 云端</span>
                  </motion.button>

                  <motion.button
                    onClick={switchToLocal}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all text-sm ${
                      mode === 'local'
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FolderOpen size={16} />
                    <span>本地视频</span>
                  </motion.button>
                </div>

                {/* 网络状态指示 */}
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${
                  networkStatus.online
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                }`}>
                  {networkStatus.online ? <Wifi size={14} /> : <WifiOff size={14} />}
                  <span>{networkStatus.online ? '网络正常' : '网络连接中断'}</span>
                </div>
              </div>
            </div>

            {/* ========== 视频播放器（单一元素，不重新挂载）========== */}
            <div className="aspect-video bg-black relative">
              {/* 关键：使用同一个 video 元素，避免重新创建 DOM */}
              <video
                ref={videoRef}
                src={currentVideoUrl || CLOUD_VIDEO_URL}
                controls
                playsInline
                preload="auto"
                className="w-full h-full"
              >
                您的浏览器不支持 HTML5 视频播放
              </video>

              {/* 加载中遮罩（初次加载 / 切换视频） */}
              {isLoading && !videoError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 text-white">
                  <Loader2 size={36} className="animate-spin mb-3" />
                  <p className="text-sm font-medium">
                    {mode === 'cloud' ? '正在加载云端视频...' : '正在加载本地视频...'}
                  </p>
                  {selectedFile && mode === 'local' && (
                    <p className="text-xs text-gray-400 mt-2 max-w-[80%] truncate">
                      {selectedFile.name}
                    </p>
                  )}
                  {retryCountRef.current > 0 && (
                    <p className="text-xs text-amber-400 mt-1">
                      第 {retryCountRef.current} 次重试
                    </p>
                  )}
                </div>
              )}

              {/* 播放中缓冲中（网络差） */}
              {isBuffering && !isLoading && !videoError && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 bg-black/70 rounded-full text-white text-xs">
                  <Loader2 size={14} className="animate-spin" />
                  正在缓冲...（网络较慢）
                </div>
              )}

              {/* 错误提示（带重试按钮） */}
              {videoError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 text-white px-6 text-center">
                  <AlertTriangle size={32} className="text-amber-400 mb-3" />
                  <h3 className="text-base font-semibold mb-2">播放失败</h3>
                  <p className="text-sm text-gray-400 mb-5 max-w-sm">{videoError.message}</p>

                  <div className="flex items-center gap-3">
                    {videoError.canRetry && (
                      <motion.button
                        onClick={handleRetry}
                        className="flex items-center gap-2 px-5 py-2 bg-white text-black rounded-xl font-medium text-sm hover:bg-gray-100"
                        whileTap={{ scale: 0.98 }}
                      >
                        <RotateCcw size={16} />
                        重试
                      </motion.button>
                    )}
                    {mode === 'local' && (
                      <motion.button
                        onClick={switchToCloud}
                        className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700"
                        whileTap={{ scale: 0.98 }}
                      >
                        <Cloud size={16} />
                        切换到云端视频
                      </motion.button>
                    )}
                  </div>

                  {!networkStatus.online && (
                    <p className="text-xs text-red-400 mt-4">⚠ 当前网络未连接</p>
                  )}
                </div>
              )}

              {/* 本地模式还没选文件的提示 */}
              {mode === 'local' && !videoBlobUrl && !videoError && !isLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 text-white px-6 text-center">
                  <FileVideo size={40} className="mb-3 text-white/80" />
                  <h3 className="text-lg font-semibold mb-2">本地视频</h3>
                  <p className="text-sm text-gray-400">
                    点击下方列表中的视频文件开始播放
                  </p>
                </div>
              )}
            </div>

            {/* ========== 视频信息 ========== */}
            <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {mode === 'cloud'
                  ? 'R2 云端视频'
                  : selectedFile?.name || '本地视频'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {mode === 'cloud'
                  ? '当前播放存储在 Cloudflare R2 上的视频文件'
                  : selectedFile
                  ? `大小：${formatSize(selectedFile.size)} · 路径：${selectedFile.path}`
                  : '请从下方列表选择视频文件'}
              </p>
            </div>

            {/* ========== 本地视频管理区域 ========== */}
            {mode === 'local' && (
              <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col">
                {/* 操作栏 */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800">
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    {/* 固定路径标签 */}
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs text-gray-600 dark:text-gray-300">
                      <FolderOpen size={14} />
                      <span className="hidden sm:inline max-w-[180px] lg:max-w-[280px] truncate">
                        {LOCAL_VIDEO_FOLDER}
                      </span>
                      <span className="sm:hidden">Steam 创意工坊</span>
                    </div>

                    {/* 重新扫描按钮 */}
                    <motion.button
                      onClick={handleScanFolder}
                      disabled={isScanning}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors active:scale-95 text-xs"
                      whileTap={{ scale: 0.98 }}
                    >
                      <RefreshCw size={14} className={isScanning ? 'animate-spin' : ''} />
                      {isScanning ? '扫描中...' : '重新扫描'}
                    </motion.button>

                    {/* 统计信息 */}
                    {files.length > 0 && (
                      <div className="flex items-center gap-2 ml-auto text-xs text-gray-600 dark:text-gray-400">
                        {scanSuccess && (
                          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <CheckCircle2 size={12} />
                            已加载
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <HardDrive size={12} />
                          {totalScanned} 个视频
                        </span>
                        <span className="text-gray-400">·</span>
                        <span>{formatSize(totalSize)}</span>
                      </div>
                    )}
                  </div>

                  {/* 搜索框 */}
                  {files.length > 0 && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                      <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={`搜索 ${totalScanned} 个视频...`}
                        className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {/* 扫描中提示 */}
                  {isScanning && !scanError && (
                    <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2.5">
                      <div className="flex items-center gap-2">
                        <Loader2 size={16} className="text-blue-600 dark:text-blue-400 animate-spin shrink-0" />
                        <p className="text-xs text-blue-700 dark:text-blue-400">
                          正在扫描文件夹，请稍候...（大文件夹可能需要较长时间）
                        </p>
                      </div>
                    </div>
                  )}

                  {/* 扫描错误提示 */}
                  {scanError && !isScanning && (
                    <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-amber-800 dark:text-amber-300 font-medium mb-1">
                            {scanError.code === 'NO_VIDEOS' ? '未找到视频' : '扫描失败'}
                          </p>
                          <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                            {scanError.message}
                          </p>
                          {scanError.canRetry && (
                            <motion.button
                              onClick={handleScanFolder}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-medium"
                              whileTap={{ scale: 0.98 }}
                            >
                              <RefreshCw size={12} />
                              重新扫描
                            </motion.button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 文件列表 */}
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 overflow-y-auto"
                >
                  {/* 空状态 */}
                  {files.length === 0 && !isScanning && !scanError && (
                    <div className="text-center py-16 px-6">
                      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HardDrive size={28} className="text-gray-400" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                        正在加载本地视频
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        正在扫描 Steam 创意工坊文件夹...
                      </p>
                      <motion.button
                        onClick={handleScanFolder}
                        className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors text-sm"
                        whileTap={{ scale: 0.98 }}
                      >
                        开始扫描
                      </motion.button>
                    </div>
                  )}

                  {/* 搜索无结果 */}
                  {files.length > 0 && filteredFiles.length === 0 && (
                    <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
                      没有找到匹配 "{searchText}" 的视频文件
                    </div>
                  )}

                  {/* 视频列表 */}
                  {filteredFiles.length > 0 && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {visibleFiles.map((file, index) => (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: Math.min(index * 0.001, 0.1), duration: 0.1 }}
                          onClick={() => handlePlayFile(file)}
                          className={`group flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 dark:hover:bg-gray-800 cursor-pointer transition-colors border-l-4 ${
                            selectedFile?.id === file.id
                              ? 'bg-green-50 dark:bg-gray-800 border-green-500'
                              : 'border-transparent'
                          }`}
                        >
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                              selectedFile?.id === file.id
                                ? 'bg-green-600 text-white'
                                : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 group-hover:bg-green-600 group-hover:text-white'
                            } transition-colors`}
                          >
                            <Play size={14} className="ml-0.5" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {file.path}
                            </p>
                          </div>

                          <div className="text-right shrink-0 text-xs text-gray-500 dark:text-gray-400">
                            <p>{formatSize(file.size)}</p>
                            <p className="text-gray-400 dark:text-gray-500 mt-0.5">
                              {formatDate(file.lastModified)}
                            </p>
                          </div>
                        </motion.div>
                      ))}

                      {/* 加载更多 */}
                      {visibleCount < filteredFiles.length && (
                        <div className="text-center py-3 text-xs text-gray-500 dark:text-gray-400">
                          <Loader2 size={12} className="inline-block animate-spin mr-2" />
                          继续滚动加载更多 ({visibleCount} / {filteredFiles.length})
                        </div>
                      )}

                      {/* 列表底部 */}
                      {visibleCount >= filteredFiles.length && filteredFiles.length > 0 && (
                        <div className="text-center py-4 text-xs text-gray-400 dark:text-gray-500">
                          ~ 已显示全部 {filteredFiles.length} 个视频 ~
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
